import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container, EmptyState } from "@/components/shared";
import ListingCard from "@/components/ListingCard";
import AdSlot from "@/components/AdSlot";
import { Compass } from "lucide-react";

const SHORES = [
  { key: "all", label: "Both shores" },
  { key: "lincolnshire", label: "Lincolnshire" },
  { key: "massachusetts", label: "Massachusetts" },
];

export default function Made() {
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shore, setShore] = useState("all");
  const [category, setCategory] = useState("all");
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sectors").then((r) => setCategories(r.data));
    api.get("/adslots", { params: { page: "made" } }).then((r) => setAds(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/listings", { params: { shore, category } })
      .then((r) => setListings(r.data))
      .finally(() => setLoading(false));
  }, [shore, category]);

  const railAd = ads.find((a) => a.slot_id === "rail.made");
  const inlineAd = ads.find((a) => a.slot_id === "card.inline");

  const featured = useMemo(
    () => listings.filter((l) => l.featured || ["beacon", "crossing"].includes(l.plan)),
    [listings]
  );

  // Build the main grid with an inline advert card at every 8th position.
  const gridItems = useMemo(() => {
    const items = [];
    listings.forEach((l, idx) => {
      items.push({ type: "listing", data: l });
      if (inlineAd && (idx + 1) % 8 === 0) items.push({ type: "ad", data: inlineAd });
    });
    return items;
  }, [listings, inlineAd]);

  return (
    <div>
      <PageHero
        kicker="Listed on both shores"
        title="The Made directory"
        intro="Every live maker and firm across both Bostons. Featured members sit at the top; every free Harbour listing stays visible below, because a working crossing keeps everyone in view."
      />

      <section className="py-12 sm:py-16">
        <Container>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between mb-10 pb-6 border-b border-[#D6D3CB]">
            <div className="flex flex-wrap gap-2" data-testid="shore-filters">
              {SHORES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`filter-shore-${s.key}`}
                  onClick={() => setShore(s.key)}
                  className={`font-sans text-sm px-4 py-2 border transition-colors ${
                    shore === s.key
                      ? "bg-[#1E1E2F] text-[#FFFAF3] border-[#1E1E2F]"
                      : "bg-[#FFFAF3] text-[#1B2430] border-[#D6D3CB] hover:border-[#CDA434]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="font-sans text-xs uppercase tracking-wider text-[#505A68]">Sector</label>
              <select
                data-testid="filter-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2 text-sm text-[#1B2430] font-sans"
              >
                <option value="all">All sectors</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-9">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-80 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />
                  ))}
                </div>
              ) : listings.length === 0 ? (
                <EmptyState
                  testId="made-empty"
                  icon={<Compass size={22} />}
                  heading="No listings on this heading"
                  text="No makers match this shore and trade just yet. Try a wider filter, or list your own business to be the first."
                />
              ) : (
                <>
                  {/* Featured row */}
                  {featured.length > 0 && (
                    <div className="mb-12">
                      <div className="flex items-center gap-3 mb-5">
                        <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#CDA434] font-semibold">
                          Featured makers
                        </span>
                        <div className="flex-1 h-px bg-[#CDA434]/40" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featured.map((l) => (
                          <ListingCard key={`f-${l.id}`} listing={l} featured />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Full grid */}
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#505A68] font-semibold">
                      All live listings
                    </span>
                    <div className="flex-1 h-px bg-[#D6D3CB]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gridItems.map((item, i) =>
                      item.type === "ad" ? (
                        <AdSlot key={`ad-${i}`} slot={item.data} variant="card" />
                      ) : (
                        <ListingCard key={item.data.id} listing={item.data} />
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Right rail advert — desktop only */}
            <div className="hidden lg:block lg:col-span-3">
              {railAd && <AdSlot slot={railAd} variant="rail" />}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
